import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, CircleNotch, Key, LinkSimple, DownloadSimple, Pencil,
  CheckCircle, XCircle, Coins, Buildings, Power, Trash, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

const COMMISSION_STATUSES = ["pending", "liberable", "paid", "cancelled"];
const STATUS_LABEL = {
  pending: "Em carência",
  liberable: "Liberada",
  paid: "Paga",
  cancelled: "Cancelada",
};
const STATUS_STYLE = {
  pending:   "bg-amber-500/10 text-amber-300",
  liberable: "bg-emerald-500/10 text-emerald-300",
  paid:      "bg-sky-500/10 text-sky-300",
  cancelled: "bg-slate-500/10 text-slate-400",
};

const inputCls = "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-white/30 transition-colors";

function Section({ title, icon: Icon, action, children, testid }) {
  return (
    <section
      className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden"
      data-testid={testid}
    >
      <header className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
        <h3 className="font-heading text-[13px] font-semibold tracking-tight flex items-center gap-2">
          {Icon && <Icon size={14} weight="regular" className="text-slate-500" />}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  );
}

function AccountPanel({ repId, repName, refresh }) {
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/admin/representatives/${repId}/account`)
      .then(({ data }) => {
        setInfo(data);
        setEmail(data.email || "");
      });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [repId]);

  const create = async (e) => {
    e.preventDefault();
    if (!email || !password || password.length < 8) {
      toast.error("Email e senha (≥ 8) são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/representatives/${repId}/account`, {
        email, password, name: repName,
      });
      // Email status surfaced from the backend response. In preview the
      // provider is LogOnly so `mode === 'log_only'` — still tell the
      // operator the email was 'simulated' so it's clear no real inbox
      // was hit, but the workflow succeeded.
      const action = info?.has_account ? "Senha atualizada" : "Conta de portal criada";
      if (data.email_sent && data.email_mode === "sent") {
        toast.success(`${action}. Email de credenciais enviado para ${email}.`);
      } else if (data.email_sent && data.email_mode === "log_only") {
        toast.success(
          `${action}. (Resend não configurado — credenciais foram logadas; envie manualmente.)`,
        );
      } else {
        toast.success(`${action}. Email NÃO foi enviado — verifique os logs.`);
      }
      setPassword("");
      load();
      refresh?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    if (!window.confirm("Bloquear o acesso desse representante ao portal?")) return;
    try {
      await api.post(`/admin/representatives/${repId}/account/disable`);
      toast.success("Acesso bloqueado.");
      load();
    } catch {
      toast.error("Falha ao bloquear.");
    }
  };

  if (info === null) {
    return (
      <div className="p-6 text-center">
        <CircleNotch size={16} weight="regular" className="text-slate-500 animate-spin inline-block" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4">
      {info.has_account ? (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
              Conta do portal
            </div>
            <div className="text-white text-sm mt-1">{info.email}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              {info.active ? (
                <span className="inline-flex items-center gap-1 text-emerald-400">
                  <CheckCircle size={11} weight="regular" /> Acesso ativo
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-rose-400">
                  <XCircle size={11} weight="regular" /> Acesso bloqueado
                </span>
              )}
              {" · "}último login: {info.last_login_at ? fmtDate(info.last_login_at) : "nunca"}
            </div>
          </div>
          {info.active && (
            <button
              onClick={disable}
              data-testid="rep-account-disable"
              className="text-[11px] text-rose-300 hover:text-rose-200 border border-rose-500/20 hover:border-rose-500/40 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
            >
              <Power size={12} weight="regular" /> Bloquear acesso
            </button>
          )}
        </div>
      ) : (
        <div className="text-sm text-amber-300 bg-amber-500/5 border border-amber-500/15 rounded-md p-3">
          Nenhuma conta de portal criada. Defina email e senha abaixo para liberar
          o acesso ao Portal do Representante.
        </div>
      )}
      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rep-account-form">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
            Email de acesso
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="rep-account-email"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
            {info.has_account ? "Nova senha" : "Senha inicial"}
          </span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            placeholder="mín. 8 caracteres"
            required
            data-testid="rep-account-password"
            className={inputCls}
          />
        </label>
        <div className="sm:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="rep-account-submit"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 transition-colors"
          >
            {saving ? "Salvando…" : info.has_account ? "Resetar senha" : "Criar conta"}
            {!saving && <Key size={14} weight="regular" />}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditRepForm({ rep, onSaved }) {
  const [form, setForm] = useState({
    name: rep.name || "",
    email: rep.email || "",
    slug: rep.slug || "",
    city: rep.city || "",
    state: rep.state || "",
    region: rep.region || "",
    phone: rep.phone || "",
    status: rep.status || "active",
    default_commission_pct: String(((rep.default_commission_pct || 0) * 100).toFixed(0)),
    notes: rep.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const pctNum = parseFloat(String(form.default_commission_pct).replace(",", "."));
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        slug: form.slug.trim().toLowerCase(),
        city: form.city.trim() || null,
        state: form.state.trim().toUpperCase() || null,
        region: form.region.trim() || null,
        phone: form.phone.trim() || null,
        status: form.status,
        default_commission_pct: pctNum / 100,
        notes: form.notes.trim() || null,
      };
      const { data } = await api.patch(`/admin/representatives/${rep.id}`, payload);
      toast.success("Representante atualizado.");
      onSaved(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rep-edit-form">
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Nome</span>
        <input value={form.name} onChange={set("name")} required data-testid="rep-edit-name" className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Email</span>
        <input type="email" value={form.email} onChange={set("email")} required data-testid="rep-edit-email" className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Slug</span>
        <input value={form.slug} onChange={set("slug")} required data-testid="rep-edit-slug" className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Status</span>
        <select value={form.status} onChange={set("status")} data-testid="rep-edit-status" className={inputCls}>
          <option value="active">Ativo</option>
          <option value="inactive">Inativo</option>
        </select>
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Cidade</span>
        <input value={form.city} onChange={set("city")} className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">UF</span>
        <input value={form.state} onChange={set("state")} maxLength={2} className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Região</span>
        <input value={form.region} onChange={set("region")} className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Telefone</span>
        <input value={form.phone} onChange={set("phone")} className={inputCls} />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Comissão %</span>
        <input value={form.default_commission_pct} onChange={set("default_commission_pct")} required data-testid="rep-edit-pct" className={inputCls} />
      </label>
      <label className="block sm:col-span-2">
        <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-1.5">Notas internas</span>
        <textarea value={form.notes} onChange={set("notes")} rows={2} className={inputCls + " resize-none"} />
      </label>
      <div className="sm:col-span-2 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="rep-edit-submit"
          className="inline-flex items-center gap-2 bg-white text-slate-900 px-4 py-2 rounded-md text-sm font-semibold hover:bg-slate-200 disabled:opacity-60 transition-colors"
        >
          {saving ? "Salvando…" : "Salvar alterações"}
          {!saving && <Pencil size={14} weight="regular" />}
        </button>
      </div>
    </form>
  );
}

function CommissionRow({ c, onChange }) {
  const [saving, setSaving] = useState(false);
  const change = async (newStatus) => {
    setSaving(true);
    try {
      await api.patch(`/admin/commissions/${c.id}`, { status: newStatus });
      toast.success(`Comissão marcada como ${STATUS_LABEL[newStatus] || newStatus}.`);
      onChange?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao atualizar.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr className="border-t border-white/5" data-testid={`commission-row-${c.id}`}>
      <td className="px-4 py-3 text-[12px] text-slate-300 font-mono">
        {c.stripe_invoice_id || "—"}
      </td>
      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
        {fmtBRL(c.amount_paid_cents)}
      </td>
      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">
        {((c.commission_pct || 0) * 100).toFixed(0)}%
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-emerald-400 font-semibold">
        {fmtBRL(c.commission_cents)}
      </td>
      <td className="px-4 py-3 text-[12px] text-slate-400">{fmtDate(c.paid_at)}</td>
      <td className="px-4 py-3 text-[12px] text-slate-400">{fmtDate(c.liberable_at)}</td>
      <td className="px-4 py-3">
        <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded-full ${STATUS_STYLE[c.status] || ""}`}>
          {STATUS_LABEL[c.status] || c.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <select
          value={c.status}
          disabled={saving}
          onChange={(e) => change(e.target.value)}
          data-testid={`commission-status-${c.id}`}
          className="bg-white/5 border border-white/10 rounded-md text-[11px] text-white px-2 py-1 focus:outline-none focus:border-white/30 disabled:opacity-50"
        >
          {COMMISSION_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
          ))}
        </select>
      </td>
    </tr>
  );
}

export default function AdminRepresentativeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const load = () => {
    setRep(null);
    api.get(`/admin/representatives/${id}`).then(({ data }) => setRep(data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!rep) {
    return (
      <div className="px-4 md:px-8 py-10 md:py-12 text-center">
        <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
      </div>
    );
  }

  const publicLink = `${window.location.origin}/r/${rep.slug}`;
  const copyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast.success("Link copiado.");
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/admin/representatives/${id}/${kind}.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${rep.slug}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao baixar CSV.");
    }
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1280px] mx-auto" data-testid="admin-rep-detail">
      <button
        onClick={() => navigate("/admin-master/representantes")}
        className="text-[12px] text-slate-400 hover:text-white inline-flex items-center gap-1.5 mb-4"
        data-testid="rep-detail-back"
      >
        <ArrowLeft size={12} weight="regular" /> Voltar
      </button>

      <header className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Representante
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
            {rep.name}
          </h1>
          <div className="text-sm text-slate-400 mt-1.5 flex items-center gap-2 flex-wrap">
            <a
              href={publicLink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[12px] text-emerald-300 hover:text-emerald-200 hover:underline underline-offset-2 break-all"
              data-testid="rep-detail-public-link"
              title="Abrir link de indicação em nova aba"
            >
              {publicLink}
            </a>
            <span>·</span>
            <span>{rep.email}</span>
            {(rep.city || rep.state) && <><span>·</span><span>{[rep.city, rep.state].filter(Boolean).join(" / ")}</span></>}
            <span>·</span>
            <span className={rep.status === "active" ? "text-emerald-400" : "text-slate-500"}>
              {rep.status === "active" ? "ativo" : "inativo"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={copyLink}
            data-testid="rep-copy-public-link"
            className="text-[12px] text-slate-300 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            <LinkSimple size={12} weight="regular" /> Copiar link
          </button>
          <button
            onClick={() => download("clients")}
            data-testid="rep-export-clients"
            className="text-[12px] text-slate-300 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            <DownloadSimple size={12} weight="regular" /> CSV clientes
          </button>
          <button
            onClick={() => download("commissions")}
            data-testid="rep-export-commissions"
            className="text-[12px] text-slate-300 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            <DownloadSimple size={12} weight="regular" /> CSV comissões
          </button>
          <button
            onClick={() => setShowDelete(true)}
            data-testid="rep-delete-btn"
            className="text-[12px] text-rose-300 hover:text-rose-100 border border-rose-500/30 hover:border-rose-500/60 hover:bg-rose-500/10 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors"
          >
            <Trash size={12} weight="regular" /> Excluir
          </button>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Clientes (ativos/total)" value={`${rep.active_clients}/${rep.total_clients}`} testid="rep-kpi-clients" />
        <Kpi label="Em carência" value={fmtBRL(rep.commission_pending_cents)} tone="text-amber-300" testid="rep-kpi-pending" />
        <Kpi label="A liberar" value={fmtBRL(rep.commission_liberable_cents)} tone="text-emerald-400" testid="rep-kpi-liberable" />
        <Kpi label="Pagas" value={fmtBRL(rep.commission_paid_cents)} tone="text-sky-300" testid="rep-kpi-paid" />
      </div>

      <div className="grid grid-cols-1 gap-5">
        <Section
          title="Conta do portal"
          icon={Key}
          testid="rep-portal-section"
        >
          <AccountPanel repId={id} repName={rep.name} refresh={load} />
        </Section>

        <Section
          title="Dados do representante"
          icon={Pencil}
          testid="rep-edit-section"
          action={
            <button
              onClick={() => setEditing((v) => !v)}
              data-testid="rep-edit-toggle"
              className="text-[11px] text-slate-300 hover:text-white"
            >
              {editing ? "fechar" : "editar"}
            </button>
          }
        >
          {editing ? (
            <EditRepForm rep={rep} onSaved={(updated) => { setRep((prev) => ({ ...prev, ...updated })); setEditing(false); load(); }} />
          ) : (
            <dl className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Field k="Telefone" v={rep.phone} />
              <Field k="Cidade" v={rep.city} />
              <Field k="UF" v={rep.state} />
              <Field k="Região" v={rep.region} />
              <Field k="Comissão padrão" v={`${((rep.default_commission_pct || 0) * 100).toFixed(0)}%`} accent="text-emerald-400" />
              <Field k="Notas internas" v={rep.notes} />
            </dl>
          )}
        </Section>

        <Section
          title={`Clientes atribuídos (${rep.clients?.length || 0})`}
          icon={Buildings}
          testid="rep-clients-section"
        >
          {(!rep.clients || rep.clients.length === 0) ? (
            <div className="p-8 text-center text-slate-500 text-sm space-y-2.5">
              <div>Nenhum cliente ainda. Compartilhe o link exclusivo:</div>
              <div className="inline-flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 max-w-full">
                <a
                  href={publicLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[12px] text-emerald-300 hover:text-emerald-200 break-all"
                  data-testid="rep-empty-state-link"
                >
                  {publicLink}
                </a>
                <button
                  type="button"
                  onClick={copyLink}
                  className="text-[11px] text-slate-400 hover:text-white border-l border-white/10 pl-2 shrink-0"
                  data-testid="rep-empty-state-copy"
                >
                  copiar
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-white/[0.02] text-left">
                  <tr>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Empresa</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Plano</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Mensalidade</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Ativação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rep.clients.map((c) => (
                    <tr key={c.tenant_id} className="border-t border-white/5" data-testid={`rep-client-${c.tenant_id}`}>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{c.company_name}</div>
                        {c.phone && <div className="text-[11px] text-slate-500">{c.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-slate-300">{c.status}</td>
                      <td className="px-4 py-3 text-[12px] text-slate-300">{c.plan_code || "—"}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {c.monthly_amount_cents > 0 ? (
                          <span className="text-emerald-400">{fmtBRL(c.monthly_amount_cents)}</span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-slate-400">
                        {fmtDate(c.activated_at || c.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin-master/tenants/${c.tenant_id}`}
                          className="text-[11px] text-slate-300 hover:text-white"
                        >
                          abrir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section
          title={`Comissões (${rep.commissions?.length || 0})`}
          icon={Coins}
          testid="rep-commissions-section"
        >
          {(!rep.commissions || rep.commissions.length === 0) ? (
            <p className="p-8 text-center text-slate-500 text-sm">
              Nenhuma comissão gerada ainda. Geradas automaticamente a cada pagamento
              de assinatura dos clientes vinculados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-white/[0.02] text-left">
                  <tr>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Invoice</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Base</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">%</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Comissão</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Pago</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Libera</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
                    <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.commissions.map((c) => (
                    <CommissionRow key={c.id} c={c} onChange={load} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {showDelete && (
        <DeleteRepModal
          rep={rep}
          onClose={() => setShowDelete(false)}
          onDeleted={() => navigate("/admin-master/representantes")}
        />
      )}
    </div>
  );
}

/**
 * DeleteRepModal — Confirmação em 2 etapas para excluir representante.
 *
 * Etapa 1: aviso do impacto (clientes desassociados, comissões preservadas,
 *          login removido) + campo pra digitar o NOME exato do rep.
 * Etapa 2 (só se o backend retornar 409 has_linked_tenants): pergunta
 *          se quer forçar exclusão mesmo assim.
 *
 * Ação é irreversível — comissões pagas ficam preservadas por conformidade
 * fiscal, mas rep_accounts e o próprio representative são deletados.
 */
function DeleteRepModal({ rep, onClose, onDeleted }) {
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkedTenants, setLinkedTenants] = useState(null);
  const [needsForce, setNeedsForce] = useState(false);
  const matches = confirmName.trim() === (rep.name || "").trim();

  const submit = async (force = false) => {
    if (!matches) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/representatives/${rep.id}`, {
        params: { confirm_name: confirmName.trim(), force },
      });
      toast.success(
        `Representante excluído. ${data.tenants_unlinked} clientes desassociados, ${data.commissions_preserved} comissões preservadas.`
      );
      onDeleted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.reason === "has_linked_tenants") {
        setLinkedTenants(detail.linked_tenants);
        setNeedsForce(true);
      } else if (detail?.reason === "name_mismatch") {
        toast.error("O nome digitado não confere.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Falha ao excluir.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className="bg-[#0F0F11] border border-rose-500/30 rounded-lg p-6 w-full max-w-md"
        data-testid="rep-delete-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-rose-500/10 border border-rose-500/20 rounded-md">
            <Warning size={16} weight="fill" className="text-rose-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-white">
              Excluir representante
            </h2>
            <p className="text-[13px] text-slate-400 mt-1">
              Ação irreversível. O login do rep será removido imediatamente.
            </p>
          </div>
        </div>

        {!needsForce ? (
          <>
            <div className="text-[12px] text-slate-400 space-y-1.5 bg-white/[0.03] border border-white/10 rounded-md p-3 mb-4">
              <div>· Clientes vinculados serão <b className="text-white">desassociados</b> (assinaturas seguem ativas)</div>
              <div>· Comissões históricas serão <b className="text-white">preservadas</b> para conformidade fiscal</div>
              <div>· Login do rep (<span className="font-mono text-rose-300">{rep.email}</span>) será <b className="text-white">removido</b></div>
              <div>· Aceites de termos legais serão removidos</div>
            </div>
            <label className="block mb-4">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-1.5">
                Digite o nome exato para confirmar
              </span>
              <div className="text-[11px] text-slate-500 mb-1.5 font-mono">{rep.name}</div>
              <input
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                data-testid="rep-delete-confirm-name"
                className={`w-full px-3 py-2 bg-white/5 border rounded-md text-sm text-white placeholder:text-slate-600 focus:outline-none transition-colors ${
                  confirmName && !matches
                    ? "border-rose-500/50"
                    : matches
                    ? "border-emerald-500/50"
                    : "border-white/10 focus:border-white/30"
                }`}
                placeholder={rep.name}
                autoFocus
              />
            </label>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="text-[13px] text-slate-400 hover:text-white px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => submit(false)}
                disabled={!matches || busy}
                data-testid="rep-delete-submit"
                className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? "Excluindo…" : "Excluir definitivamente"}
                {!busy && <Trash size={12} weight="regular" />}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-[13px] text-slate-300 bg-amber-500/10 border border-amber-500/20 rounded-md p-3 mb-4">
              Este representante tem <b className="text-amber-300">{linkedTenants} cliente(s) ativo(s)</b>.
              Prosseguir vai desassociar todos eles. As assinaturas continuam ativas,
              mas comissões futuras não serão atribuídas a este rep.
              <div className="mt-1 text-[12px] text-slate-400">Confirma?</div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="text-[13px] text-slate-400 hover:text-white px-3 py-2 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={busy}
                data-testid="rep-delete-force"
                className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60 transition-colors"
              >
                {busy ? "Excluindo…" : "Sim, forçar exclusão"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`mt-2 font-heading text-xl font-semibold tabular-nums ${tone || "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function Field({ k, v, accent }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{k}</dt>
      <dd className={`text-sm mt-1 ${accent || "text-white"}`}>{v || "—"}</dd>
    </div>
  );
}
